import type { ReactNode } from "react";
import { Button, Collapse, Container, FormControlLabel, Grid, Switch } from "@mui/material";
import PageTitle from "components/PageTitle";
import { useCallback, useEffect, useState } from "react";
import LoadingScreen from "components/Template/LoadingScreen";
import { nbsp } from "constants/InputRules";
import InformationBox from "components/InformationBox";
import { SdeaIcon, CommuneIcon } from "icons";
import MandatsInformationsList from "./components/Mandats/MandatsInformationsList";
import { elusService } from "services";
import { createSwrConfiguration, elusUtil } from "utils";
import { withPageErrorBoundary } from "utils/errorHandling";
import HigherMandatsSubtitle from "components/Elus/HigherMandatsSubtitle";
import EluContactInformationsWithErrorBoundary from "./components/ContactInformations/EluContactInformationsWithErrorBoundary";
import PersonIcon from "@mui/icons-material/Person";
import { Authorization } from "@sdeapps/react-core";
import { ApplicationRole } from "constants/ApplicationRole";
import AddIcon from "@mui/icons-material/Add";
import { useRequiredParams } from "hooks";
import PersonneSourceChip from "components/Typography/PersonneSourceChip";
import useSWR from "swr";

function getCivilite(sexe: string): string {
  return sexe === "F" ? "Mme" : "M.";
}

function EluPage(): ReactNode {
  const { id } = useRequiredParams<{ id: string }>();
  const [isModifying, setIsModifying] = useState(false);
  const [isDisplayingInstitutionnelHistorique, setIsDisplayingInstitutionnelHistorique] =
    useState(false);
  const [isDisplayingSDEAHistorique, setIsDisplayingSDEAHistorique] = useState(false);
  const [isCreatingNewSdeaMandat, setIsCreatingNewSdeaMandat] = useState(false);

  const {
    data: elu,
    isLoading,
    error,
    mutate,
  } = useSWR(`/elus/${id}`, async () => elusService.getPersonneById(id), createSwrConfiguration());

  useEffect(() => {
    if (error != null) {
      throw error as Error;
    }
  }, [error]);

  useEffect(() => {
    void mutate();
    setIsDisplayingInstitutionnelHistorique(false);
    setIsDisplayingSDEAHistorique(false);
  }, [mutate, id]);

  const refetchElu = useCallback(() => {
    void mutate();
    setIsModifying(false);
    setIsCreatingNewSdeaMandat(false);
  }, [mutate]);

  if (isLoading || elu == null) {
    return <LoadingScreen />;
  }

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2}>
        <PageTitle
          title={`${elu.sexe != null ? getCivilite(elu.sexe) + " " : ""}${elu.nom} ${elu.prenom}`}
          icon={<PersonIcon />}
          subtitle={
            <>
              Élu
              {nbsp}
              <PersonneSourceChip source={elu.source} sx={{ mt: -0.25, ml: 1 }} />
            </>
          }
        />
        <Grid container size={12}>
          <HigherMandatsSubtitle elu={elu} />
        </Grid>
        <Grid container size={12}>
          <InformationBox hasBackground icon={CommuneIcon} articleTitle="Contacts">
            <EluContactInformationsWithErrorBoundary
              elu={elu}
              isModifying={isModifying}
              setIsModifying={setIsModifying}
            />
          </InformationBox>
        </Grid>
        <Grid container size={12}>
          <InformationBox
            hasBackground
            icon={CommuneIcon}
            articleTitle="Fonctions Institutionnelles">
            <Grid size={12}>
              <MandatsInformationsList
                mandats={elu.mandats?.filter(elusUtil.filterActiveMandatsInstitutionnels)}
                typeMandats="institutionnels"
                noMandatsFallback="Cette personne n'a aucun mandat institutionnel actif"
                refetchElu={refetchElu}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDisplayingInstitutionnelHistorique}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                      setIsDisplayingInstitutionnelHistorique(event.target.checked);
                    }}
                  />
                }
                label="Afficher l'historique"
              />
            </Grid>
            <Grid size={12}>
              <Collapse in={isDisplayingInstitutionnelHistorique}>
                <MandatsInformationsList
                  mandats={elu.mandats?.filter(elusUtil.filterHistoricMandatsInstitutionnels)}
                  typeMandats="institutionnels"
                  noMandatsFallback="Cette personne n'a aucun mandat institutionnel historique"
                  refetchElu={refetchElu}
                />
              </Collapse>
            </Grid>
          </InformationBox>
        </Grid>
        <Grid container size={12}>
          <InformationBox hasBackground icon={SdeaIcon} articleTitle="Fonctions SDEA">
            <Grid size={12}>
              <MandatsInformationsList
                mandats={elu.mandats?.filter(elusUtil.filterActiveMandatsSdea)}
                typeMandats="SDEA"
                noMandatsFallback="Cette personne n'a aucun mandat SDEA actif"
                displayCreateNewMandat={isCreatingNewSdeaMandat}
                refetchElu={refetchElu}
                cancelDisplayCreateNewMandat={() => {
                  setIsCreatingNewSdeaMandat(false);
                }}
                createMandatEluId={elu.id}
              />
            </Grid>
            <Grid container size={12}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDisplayingSDEAHistorique}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setIsDisplayingSDEAHistorique(event.target.checked);
                      }}
                    />
                  }
                  label="Afficher l'historique"
                />
              </Grid>
              <Grid container size={{ xs: 12, sm: 6 }} sx={{ justifyContent: "flex-end" }}>
                <Authorization
                  roles={[ApplicationRole.ADMINISTRATOR, ApplicationRole.GESTIONNAIRE_ELECTIONS]}>
                  {!isCreatingNewSdeaMandat && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setIsCreatingNewSdeaMandat(true);
                      }}
                      startIcon={<AddIcon />}>
                      Ajouter un mandat
                    </Button>
                  )}
                </Authorization>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Collapse in={isDisplayingSDEAHistorique}>
                <MandatsInformationsList
                  mandats={elu.mandats?.filter(elusUtil.filterHistoricMandatsSdea)}
                  typeMandats="SDEA"
                  noMandatsFallback="Cette personne n'a aucun mandat SDEA historique"
                  refetchElu={refetchElu}
                />
              </Collapse>
            </Grid>
          </InformationBox>
        </Grid>
      </Grid>
    </Container>
  );
}

const EluPageWithErrorBoundary = withPageErrorBoundary(EluPage);

export default EluPageWithErrorBoundary;
